/**
 * NormaShared — shared utilities for all pages.
 * Loaded before app.js / auth-pages.js / inline scripts.
 * Exposes window.NormaShared.
 */
(function () {
  'use strict';

  var siteConfig = window.NormaConfig || {};

  // ---- Early session hint (synchronous, no await) ----
  // theme-init.js adds .has-session on <html>; we read it here to pre-render
  // the logged-in account button before first paint (avoids header CLS).
  // app.js re-renders after getSession() with proper data — no visual shift.
  try {
    if (document.documentElement.classList.contains('has-session')) {
      var _preUsername = localStorage.getItem('normagrid_username') || '';
      renderAccountButton({ user_metadata: {} }, { profile: { username: _preUsername } });
    }
  } catch (e) {}

  // ---- Helpers ----

  function escapeHtml(str) {
    if (!str) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function formatDate(iso) {
    if (!iso) return '';
    try {
      var dateStr = iso.length === 10 ? iso + 'T00:00:00' : iso;
      var d = new Date(dateStr);
      return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
    } catch (e) {
      return iso;
    }
  }

  function getStatusClass(status) {
    var map = siteConfig.STATUS_CLASS_MAP || {};
    return map[status] || '';
  }

  function getSupabaseUrl() {
    return siteConfig.SUPABASE_URL || '';
  }

  // ---- Theme ----

  function initTheme() {
    // theme-init.js already sets data-theme in <head>; skip if already applied.
    if (document.documentElement.getAttribute('data-theme')) return;
    var stored = localStorage.getItem('theme');
    var prefersDark = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
    var theme = stored || (prefersDark ? 'dark' : 'light');
    document.documentElement.setAttribute('data-theme', theme);
  }

  function toggleTheme() {
    var theme = document.documentElement.getAttribute('data-theme') || 'light';
    var next = theme === 'dark' ? 'light' : 'dark';
    document.documentElement.setAttribute('data-theme', next);
    localStorage.setItem('theme', next);
  }

  function setupThemeToggle() {
    var btn = document.getElementById('theme-toggle');
    if (btn) btn.addEventListener('click', toggleTheme);
  }

  // ---- Category Nav ----

  function renderCategoryNav(currentPageMode) {
    var nav = document.getElementById('category-nav');
    if (!nav) return;
    nav.scrollLeft = 0;
    // Skip if links are already in the HTML (pre-rendered for zero CLS).
    if (nav.querySelector('.nav-link')) {
      setupNavPrefetch();
      return;
    }

    var items = [
      { label: 'Home', href: '/', mode: 'home' },
      { label: 'Overview', href: '/overview', mode: 'overview' },
      { label: 'Glossary', href: '/glossary', mode: 'glossary' }
    ];

    for (var i = 0; i < items.length; i++) {
      var a = document.createElement('a');
      a.href = items[i].href;
      a.textContent = items[i].label;
      a.className = 'nav-link';
      if (i === 0) a.classList.add('nav-overview');
      if (currentPageMode === items[i].mode) {
        a.classList.add('active');
        a.setAttribute('aria-current', 'page');
      }
      nav.appendChild(a);
    }

    setupNavPrefetch();
  }

  // ---- Account Dropdown ----

  var USER_ICON = '<svg class="account-icon" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>';

  function renderAccountButton(user, options) {
    var opts = options || {};
    var wrap = document.getElementById('account-wrap');
    if (!wrap) return;
    wrap.innerHTML = '';
    wrap.classList.remove('open');

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.setAttribute('aria-expanded', 'false');
    btn.setAttribute('aria-haspopup', 'true');
    btn.addEventListener('click', function (e) {
      e.stopPropagation();
      wrap.classList.toggle('open');
      btn.setAttribute('aria-expanded', wrap.classList.contains('open'));
    });

    var dropdown = document.createElement('div');
    dropdown.className = 'account-dropdown';
    dropdown.setAttribute('role', 'menu');

    if (user) {
      var meta = user.user_metadata || {};
      var profile = opts.profile || {};
      var cachedUsername = '';
      try { cachedUsername = localStorage.getItem('normagrid_username') || ''; } catch (e) {}
      var displayName = profile.username || meta.username || cachedUsername || 'Account';
      btn.className = 'account-btn account-btn--logged-in';
      btn.setAttribute('aria-label', displayName);
      btn.innerHTML = '<span class="account-btn-name">' + escapeHtml(displayName) + '</span>';

      var accountLink = document.createElement('a');
      accountLink.href = '/account';
      accountLink.className = 'account-dropdown-item';
      accountLink.setAttribute('role', 'menuitem');
      accountLink.textContent = 'My Account';

      var logoutBtn = document.createElement('button');
      logoutBtn.type = 'button';
      logoutBtn.className = 'account-dropdown-item account-dropdown-item--danger';
      logoutBtn.setAttribute('role', 'menuitem');
      logoutBtn.textContent = 'Log out';
      logoutBtn.addEventListener('click', function () {
        NormaAuth.signOut().then(function () {
          if (opts.onLogout) opts.onLogout();
        });
      });

      dropdown.appendChild(accountLink);
      dropdown.appendChild(logoutBtn);
    } else {
      btn.className = 'account-btn';
      btn.setAttribute('aria-label', 'Account');
      btn.innerHTML = USER_ICON;

      var loginLink = document.createElement('a');
      loginLink.href = '/login';
      loginLink.className = 'account-dropdown-item';
      loginLink.setAttribute('role', 'menuitem');
      loginLink.textContent = 'Log in';

      var signupLink = document.createElement('a');
      signupLink.href = '/signup';
      signupLink.className = 'account-dropdown-item';
      signupLink.setAttribute('role', 'menuitem');
      signupLink.textContent = 'Sign up';

      dropdown.appendChild(loginLink);
      dropdown.appendChild(signupLink);
    }

    wrap.appendChild(btn);
    wrap.appendChild(dropdown);

    // Position dropdown centered under the button when possible, without right overflow.
    // Skip during pre-render (elements not yet painted → offsetWidth is 0).
    if (btn.offsetWidth > 0) {
      var btnCenter = btn.offsetLeft + (btn.offsetWidth / 2);
      var dropdownWidth = dropdown.offsetWidth || 160;
      var wrapWidth = wrap.clientWidth || 0;
      var left = btnCenter - (dropdownWidth / 2);
      var maxLeft = wrapWidth - dropdownWidth;
      if (maxLeft >= 0) {
        if (left < 0) left = 0;
        if (left > maxLeft) left = maxLeft;
      } else {
        left = maxLeft;
      }
      dropdown.style.left = left + 'px';
    }

    // Keyboard navigation for dropdown items
    dropdown.addEventListener('keydown', function (e) {
      var items = Array.prototype.slice.call(dropdown.querySelectorAll('[role="menuitem"]'));
      if (items.length === 0) return;
      var idx = items.indexOf(e.target);
      if (idx === -1 && e.target !== btn) return;

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        if (e.target === btn || idx === -1) { items[0].focus(); }
        else if (idx < items.length - 1) { items[idx + 1].focus(); }
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        if (idx > 0) { items[idx - 1].focus(); }
        else { btn.focus(); }
      } else if (e.key === 'Escape') {
        e.preventDefault();
        wrap.classList.remove('open');
        btn.setAttribute('aria-expanded', 'false');
        btn.focus();
      } else if (e.key === 'Home') {
        e.preventDefault();
        items[0].focus();
      } else if (e.key === 'End') {
        e.preventDefault();
        items[items.length - 1].focus();
      }
    });

    // ArrowDown from button when open → first menuitem
    btn.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowDown' && wrap.classList.contains('open')) {
        e.preventDefault();
        var items = dropdown.querySelectorAll('[role="menuitem"]');
        if (items.length > 0) items[0].focus();
      }
    });
  }

  function setupDropdownClose() {
    document.addEventListener('click', function (e) {
      var wrap = document.getElementById('account-wrap');
      if (wrap && !wrap.contains(e.target)) {
        wrap.classList.remove('open');
        var btn = wrap.querySelector('.account-btn');
        if (btn) btn.setAttribute('aria-expanded', 'false');
      }
    });
  }

  // ---- Search Redirect ----

  function setupSearchRedirect() {
    var input = document.getElementById('search');
    if (!input) return;
    input.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' && input.value.trim()) {
        window.location.href = '/overview?q=' + encodeURIComponent(input.value.trim());
      }
    });
  }

  // ---- Navigation Prefetch ----

  var PREFETCHABLE_NAV_TARGETS = {
    '/': {
      href: '/'
    },
    '/overview': {
      href: '/overview'
    },
    '/glossary': {
      href: '/glossary'
    },
  };
  var prefetchedNavTargets = {};

  function setupNavPrefetch() {
    if (!shouldWarmNavigation()) return;
    var nav = document.getElementById('category-nav');
    if (!nav || nav.dataset.prefetchBound === 'true') return;
    var links = nav.querySelectorAll ? nav.querySelectorAll('a[href]') : [];
    if (!links.length) return;

    nav.dataset.prefetchBound = 'true';
    for (var i = 0; i < links.length; i++) {
      bindPrefetchTrigger(links[i]);
    }
  }

  function shouldWarmNavigation() {
    var connection = typeof navigator !== 'undefined' ? navigator.connection : null;
    if (!connection) return true;
    if (connection.saveData) return false;
    if (connection.effectiveType === 'slow-2g' || connection.effectiveType === '2g') return false;
    return true;
  }

  function bindPrefetchTrigger(link) {
    if (!link || !link.addEventListener || !link.dataset) return;
    if (link.dataset.prefetchBound === 'true') return;
    link.dataset.prefetchBound = 'true';

    function warm() {
      prefetchNavTarget(resolvePrefetchTarget(link));
    }

    link.addEventListener('pointerenter', warm, { passive: true, once: true });
    link.addEventListener('focusin', warm, { once: true });
    link.addEventListener('touchstart', warm, { passive: true, once: true });
  }

  function resolvePrefetchTarget(link) {
    if (!link) return null;
    if (typeof link.getAttribute === 'function') {
      return link.getAttribute('href');
    }
    return link.href || null;
  }

  function normalizePrefetchPath(href) {
    if (!href || typeof window === 'undefined' || !window.location) return null;
    try {
      var url = new URL(href, window.location.origin);
      if (url.origin !== window.location.origin) return null;
      var path = url.pathname || '/';
      if (path === '/index.html') return '/';
      if (path.length > 1 && path.charAt(path.length - 1) === '/') {
        path = path.slice(0, -1);
      }
      return path || '/';
    } catch (error) {
      return null;
    }
  }

  function prefetchNavTarget(href) {
    var path = normalizePrefetchPath(href);
    if (!path || prefetchedNavTargets[path]) return;

    var target = PREFETCHABLE_NAV_TARGETS[path];
    if (!target) return;

    prefetchedNavTargets[path] = true;
    ensureResourceHint('prefetch', target.href, 'document');
    if (!target.assets || !target.assets.length) return;

    for (var i = 0; i < target.assets.length; i++) {
      ensureResourceHint('prefetch', target.assets[i].href, target.assets[i].as);
    }
  }

  function ensureResourceHint(rel, href, asValue) {
    if (!document.head || !rel || !href) return;
    if (findResourceHint(rel, href)) return;

    var link = document.createElement('link');
    link.rel = rel;
    link.href = href;
    if (asValue) link.as = asValue;
    if (asValue === 'fetch') link.crossOrigin = 'anonymous';
    document.head.appendChild(link);
  }

  function findResourceHint(rel, href) {
    if (!document.head || !document.head.querySelectorAll) return null;
    var links = document.head.querySelectorAll('link');
    for (var i = 0; i < links.length; i++) {
      if (links[i].rel === rel && links[i].href === href) return links[i];
    }
    return null;
  }

  // ---- Footer ----

  function syncSiteFooterYear() {
    var year = String(new Date().getFullYear());
    var years = document.querySelectorAll('[data-site-footer-year]');
    for (var i = 0; i < years.length; i++) {
      years[i].textContent = year;
    }
  }

  // ---- Expose ----

  window.escapeHtml = escapeHtml;

  window.NormaShared = {
    escapeHtml: escapeHtml,
    formatDate: formatDate,
    initTheme: initTheme,
    toggleTheme: toggleTheme,
    setupThemeToggle: setupThemeToggle,
    renderCategoryNav: renderCategoryNav,
    renderAccountButton: renderAccountButton,
    setupDropdownClose: setupDropdownClose,
    setupSearchRedirect: setupSearchRedirect,
    setupNavPrefetch: setupNavPrefetch,
    syncSiteFooterYear: syncSiteFooterYear,
    getStatusClass: getStatusClass,
    getSupabaseUrl: getSupabaseUrl
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      syncSiteFooterYear();
      setupNavPrefetch();
    });
  } else {
    syncSiteFooterYear();
    setupNavPrefetch();
  }
})();
